/**
 * Which transaction is a property's LATEST SALE - and which rows only look like sales.
 *
 * ⛔⛔ THE 101ST, FOUND BY LOOKING AT A RECORD PD9 HAD JUST MERGED. The property page took
 *    `transactions[0]` of an endpoint sorted `transaction_date desc`, and Postgres puts undated rows
 *    FIRST on a descending sort. CoStar stores an open listing as an undated 'sale' with a live status,
 *    so the card showed the listing: 305 pages read "No sale on record" beside a closed sale, and 135
 *    printed a live listing's ASK under "Latest sale" - an ask quoted as a trade (all 13,973
 *    transactions, measured 2026-09-23). The history rows already knew the rule; the card did not use it.
 */
export interface SaleLike {
  transaction_type: string;
  transaction_date?: string | null;
  recording_date?: string | null;
  sale_price?: number | null;
  for_sale_status?: string | null;
}

/** CoStar's sale statuses for a property still on the market. */
const LIVE_SALE_STATUSES = ['active', 'under contract', 'escrow', 'pending'];

/**
 * A "sale" row with no closing date but a live CoStar status is an OPEN LISTING, not a closed comp -
 * and for these rows CoStar puts the ASKING price in sale_price.
 */
export const isLiveListing = (tx: SaleLike): boolean =>
  tx.transaction_type === 'sale' &&
  !tx.transaction_date &&
  LIVE_SALE_STATUSES.includes(String(tx.for_sale_status || '').toLowerCase().trim());

/** A closed sale: a priced 'sale' that is not an open listing. */
export const isClosedSale = (tx: SaleLike): boolean =>
  tx.transaction_type === 'sale' && Boolean(tx.sale_price) && !isLiveListing(tx);

const dateOf = (tx: SaleLike): string => tx.transaction_date || tx.recording_date || '';

/** The newest closed sale, or null. A dated sale beats an undated one, whatever order the rows came in. */
export const latestClosedSale = <T extends SaleLike>(txs: readonly T[]): T | null => {
  const sales = txs.filter(isClosedSale);
  if (sales.length === 0) return null;
  return [...sales].sort((a, b) => dateOf(b).localeCompare(dateOf(a)))[0];
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * 'YYYY-MM-DD...' -> 'Oct 2021', read from the digits so no timezone can move the month; null otherwise.
 * ⭐ B-TX2 (b) (the 101st): a sale shows WHEN it closed, so an old price cannot read as current.
 */
export const monthYear = (d: string | null | undefined): string | null => {
  const m = /^(\d{4})-(\d{2})-\d{2}/.exec(d ?? '');
  return m ? `${MONTHS[Number(m[2]) - 1]} ${m[1]}` : null;
};
